import React, {useCallback, useEffect, useState} from 'react'
import {Box} from '@mui/material'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import Button from 'src/components-new/primitives/Button'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {buildColumns, getActiveColumnIds, MaterialRequestRow} from './materialRequestsColumns'
import MaterialRequestDialogNew, {MaterialRequestFormValues} from './MaterialRequestDialogNew'

// Same filterConfig as the legacy page (src/pages/materialRequests/MaterialRequests.tsx) —
// hardcoded, not PSP-driven. Each filter is still tied to a column via FILTER_COLUMN_ID
// below, so a filter never outlives the column it searches into.
const FILTER_CONFIG: FilterItem[] = [
  {id: 'name', label: 'Nome', type: 'string'},
  {id: 'requestDate', label: 'Data richiesta', type: 'rangePicker'},
]

// Maps a filter's id to the column id it depends on, for the one filter whose id doesn't
// match its column 1:1 (the "Nome" filter searches on `name`, but the table column is
// `materialName`). The "Data richiesta" filter's id already equals its column id.
const FILTER_COLUMN_ID: Record<string, string> = {
  name: 'materialName',
}

/**
 * New-style equivalent of src/pages/materialRequests/MaterialRequests.tsx.
 * No page title is rendered here — HeaderBar derives it from the "materialRequests" nav
 * item label — and there is no row click / detail action, matching the legacy behavior
 * (only the "Richiedi materiale" button that opens the creation dialog).
 */
export default function MaterialRequestsListNew(): JSX.Element {
  const columnOptions = useAppSelector(
    state => state.pspConfigReducer.data.materialRequestsConfigurations?.columnOptions
  )
  const toast = useToast()

  const [rows, setRows] = useState<MaterialRequestRow[]>([])
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [activeFilters, setActiveFilters] = useState<FilterResult[]>([])
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: '-requestDate'})
  const [requestOpen, setRequestOpen] = useState(false)

  const fetchData = useCallback(() => {
    // `requestDate` comes from the Filters' `rangePicker` type as a [fromISO, toISO] pair
    // (see FilterResult shape in Filters.tsx) — same mapping the legacy fetchData does.
    const dateFilter = activeFilters.find(f => f.property === 'requestDate' && f.type === 'rangePicker')
    apiClient.getMaterialRequests(
      {
        name: activeFilters.find(f => f.property === 'name')?.value,
        dateFrom: dateFilter?.value?.[0],
        dateTo: dateFilter?.value?.[1],
        size: pagination.rowsPerPage,
        page: pagination.page,
        sort: pagination.order || undefined,
      },
      setStatus
    )
      .then((data: any) => {
        if (Array.isArray(data)) setRows(data)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [activeFilters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setActiveFilters(values)
    setPagination(prev => ({...prev, page: 0}))
  }

  const handleSaveRequest = async ({materialId, materialName, code, requestedQuantity}: MaterialRequestFormValues) => {
    let finalStatus: string | undefined
    await apiClient.postMaterialRequest({materialId, materialName, code, requestedQuantity}, (s: string) => { finalStatus = s })
    if (finalStatus === STATUS.FULFILLED) {
      toast.success('Richiesta inviata con successo.')
      setRequestOpen(false)
      fetchData()
    } else {
      toast.error("Errore nell'invio della richiesta.")
    }
  }

  const columns: ColumnDef<MaterialRequestRow>[] = buildColumns(columnOptions)

  // Only offer a filter if its column is actually shown — e.g. if a PSP config disables
  // the "Data della richiesta" column, the "Data richiesta" filter disappears from the
  // drawer too, rather than letting a PSP admin filter on data the table never displays.
  const activeColumnIds = getActiveColumnIds(columnOptions)
  const filterConfig = FILTER_CONFIG.filter(item => activeColumnIds.has(FILTER_COLUMN_ID[item.id] ?? item.id))

  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2, flexWrap: 'wrap'}}>
        <Button onClick={() => setRequestOpen(true)} variant="primary">
          {'Richiedi materiale'}
        </Button>
        <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />
      </Box>

      {status === STATUS.REJECTED && (
        <InlineMessage severity="error" onClose={() => setStatus(STATUS.FULFILLED)} sx={{mb: 2}}>
          {'Errore nel caricare le richieste materiali.'}
        </InlineMessage>
      )}

      <DataTable<MaterialRequestRow>
        columns={columns}
        rows={rows}
        keyField="_id"
        loading={status === STATUS.PENDING}
        emptyMessage="Nessuna richiesta trovata"
        // No dedicated count endpoint exists for material requests (`getMaterialRequestsCount`
        // is not part of the API) — the legacy page (src/pages/materialRequests/MaterialRequests.tsx)
        // works around this the same way, using the current page's row count as the total.
        // This is a known backend limitation, not an oversight — do not invent a count call.
        totalElements={rows.length}
        pagination={pagination}
        onPaginationChange={setPagination}
        sortBy={pagination.order}
        onSortChange={(order) => setPagination(prev => ({...prev, order, page: 0}))}
      />

      {requestOpen && (
        <MaterialRequestDialogNew
          onClose={() => setRequestOpen(false)}
          onSave={handleSaveRequest}
          open={requestOpen}
        />
      )}
    </Box>
  )
}
